import type { CurrencyData, OptionCategoryData, StoreItemData } from "@hitit/api";
import OptionCategory from "./OptionCategory";
import { TranslationDTO } from "../TranslationDTO";
import type Category from "./Category";

export type ConvinienceConstructorArg = {
    id: string
    name: string,
    price: number,
    description?: string,
    optionCollection: OptionCategoryData[];
    stock?: number,
    barcode?: string,
    placeEnabled: boolean,
    displaySort: number
}

export default class StoreItem {

    cateogry: Category
    translationDTOs: TranslationDTO[]
    id?: string;
    uid?: string;
    enabled: boolean;
    displaySort: number;
    taxPercent: number;
    taxable: boolean;
    priceCurrency: CurrencyData;
    price: number;
    inventory: number;
    takeoutable: boolean;
    deliverable: boolean;
    sellableInStore: boolean;
    sellableOnline: boolean;
    placeEnabled: boolean;
    preOrderable: boolean;
    validFromDate: string;
    validUntil: string;
    private optionCollectionDTOs: OptionCategory[];

    static convienceArg(displaySort: number, id: string): ConvinienceConstructorArg {
        return {
            name: "",
            displaySort,
            optionCollection: [],
            placeEnabled: false,
            price: 0,
            stock: 0,
            barcode: "",
            description: "",
            id: id
        }
    }

    static convinienceConstructor(arg: ConvinienceConstructorArg, lang = "ko", category: Category) {
        const { id, name, displaySort, price, description, optionCollection, stock, barcode, placeEnabled } = arg

        const translation = new TranslationDTO(name, lang, true, "", description)
        const storeItemDTO: StoreItemData = {
            translationDTOs: [translation],
            id: id,
            uid: undefined,
            enabled: true,
            displaySort: displaySort,
            taxPercent: 10,
            taxable: true,
            priceCurrency: "KRW",
            price: price,
            inventory: 0,
            takeoutable: false,
            deliverable: false,
            sellableInStore: false,
            sellableOnline: false,
            placeEnabled: placeEnabled,
            preOrderable: false,
            validFromDate: (new Date()).toISOString(),
            validUntil: "",
            optionCollectionDTOs: optionCollection
        }

        return new StoreItem(storeItemDTO, category)
    }

    constructor(storeItemData: StoreItemData, category: Category) {
        this.translationDTOs = storeItemData.translationDTOs
        this.id = storeItemData.id
        this.uid = storeItemData.uid
        this.enabled = storeItemData.enabled
        this.displaySort = storeItemData.displaySort
        this.taxPercent = storeItemData.taxPercent
        this.taxable = storeItemData.taxable
        this.priceCurrency = storeItemData.priceCurrency
        this.price = storeItemData.price
        this.inventory = storeItemData.inventory
        this.takeoutable = storeItemData.takeoutable
        this.deliverable = storeItemData.deliverable
        this.sellableInStore = storeItemData.sellableInStore
        this.sellableOnline = storeItemData.sellableOnline
        this.placeEnabled = storeItemData.placeEnabled || false
        this.preOrderable = storeItemData.preOrderable
        this.validFromDate = storeItemData.validFromDate
        this.validUntil = storeItemData.validUntil || ""
        this.optionCollectionDTOs = storeItemData.optionCollectionDTOs.map(v => new OptionCategory(v))
        this.cateogry = category
    }

    public getOptionCollectionDTOs() {
        return [...this.optionCollectionDTOs]
    }

    public getTranslation() {
        return this.translationDTOs
    }

    public getPrice() {
        return this.price
    }

    public getStockAvailable() {
        return true
    }

    public getStock() {
        return this.inventory
    }

    public getBarcodeAvailable() {
        return false
    }

    public getBarcode(): null | string {
        return null
    }

    public getId() {
        return this.id
    }

    public setOption(option: OptionCategory) {
        this.optionCollectionDTOs = [...this.optionCollectionDTOs.filter((v => v.getId() !== option.getId())), option]
    }

    public deleteOption(optionId: string) {
        this.optionCollectionDTOs = this.optionCollectionDTOs.filter(v => v.getId() !== optionId)
    }

    public setTranslation(translation: TranslationDTO) {
        this.translationDTOs = [
            ...this.translationDTOs.filter(v => v.lang !== translation.lang),
            translation
        ]
    }

    public getClone() {
        const arg: StoreItemData = {
            translationDTOs: this.translationDTOs as any,
            id: this.id!,
            uid: this.uid,
            enabled: this.enabled,
            displaySort: this.displaySort,
            taxPercent: this.taxPercent,
            taxable: this.taxable,
            priceCurrency: this.priceCurrency,
            price: this.price,
            inventory: this.inventory,
            takeoutable: this.takeoutable,
            deliverable: this.deliverable,
            sellableInStore: this.sellableInStore,
            sellableOnline: this.sellableOnline,
            placeEnabled: this.placeEnabled,
            preOrderable: this.preOrderable,
            validFromDate: this.validFromDate,
            validUntil: this.validUntil,
            optionCollectionDTOs: this.optionCollectionDTOs.map(v => v.getOptionCategoryDTO())
        }

        return new StoreItem(arg, this.cateogry)
    }

    public getStoreItemDTO() {
        const reg = /^\|TEMP\|/;
        const dto: StoreItemData = {
            translationDTOs: this.translationDTOs as any,
            id: reg.test(this.id!) ? undefined : this.id,
            uid: reg.test(this.id!) ? undefined : this.uid,
            enabled: false,
            displaySort: this.displaySort,
            taxPercent: this.taxPercent,
            taxable: this.taxable,
            priceCurrency: this.priceCurrency,
            price: this.price,
            inventory: this.inventory,
            takeoutable: this.takeoutable,
            deliverable: this.deliverable,
            sellableInStore: this.sellableInStore,
            sellableOnline: this.sellableOnline,
            placeEnabled: this.placeEnabled,
            preOrderable: this.preOrderable,
            validFromDate: this.validFromDate,
            validUntil: this.validUntil,
            optionCollectionDTOs: this.optionCollectionDTOs.map((v) => v.getOptionCategoryDTO())
        }
        return dto
    }

    public sortOptionCategory() {
        return this.optionCollectionDTOs.sort((a, b) => a.displaySort - b.displaySort)
            .map((v) => {
                v.sortOptionItem()
                return v
            })
    }

}